// src/utils/froggerStorageKeys.js
//
// EVERY FROGGERAI / FROGGY U BROWSER-STORAGE KEY IS BUILT HERE, AND EVERY
// ONE CARRIES THE SIGNED-IN USER (R-TRAINING junior Cowork run on beta
// dd5a4e14, ruled 2026-11-04, fix 1).
//
// An Owner signed out and a junior signed in on the same browser; the
// junior's Learn panel showed the Owner's conversation — with a live Undo
// on the Owner's write — and the Owner's sticky place ("you're at Rack
// 2"). The keys were per facility and per tab, never per person, and
// sign-out clears localStorage while these live in sessionStorage. The
// server already refused the Undo (`undo._authorise`); this was a DISPLAY
// leak, and the fix is that a key names whose it is.
//
//   - Every key is `ff.froggerai.<user part>.<what>`; the user part is
//     `u<id>`, or `anon` when nobody is signed in.
//   - `clearForeignFroggerKeys(userId)` drops every `ff.froggerai.*` key
//     that is not this user's (all of them when `userId` is null), plus
//     the pre-fix unkeyed shapes. It is called from ONE place: the
//     AuthProvider, whenever the signed-in user changes (sign-in as
//     someone else, sign-out, the first hydrate after a reload).
//   - No component builds a key by hand: a sweep
//     (`__tests__/froggerStorageKeys.test.js`) fails on a literal
//     `ff.froggerai.` anywhere else in src.

export const FROGGER_KEY_PREFIX = 'ff.froggerai.';

/** Key shapes from before the keys carried a user — always foreign now. */
const LEGACY_KEY_PREFIXES = ['froggerai_cid_'];

/** The last part of every user's sticky-place key. */
export const STICKY_PLACE_SUFFIX = 'place.sticky';

/** Is `key` somebody's sticky place? (setupTests clears them per test.) */
export const isStickyPlaceKey = (key) => {
  const k = String(key || '');
  return k.startsWith(FROGGER_KEY_PREFIX) && k.endsWith(`.${STICKY_PLACE_SUFFIX}`);
};

/** The user part of a key: `u<id>`, or `anon` with nobody signed in. */
export const userPart = (userId) => (
  userId === null || userId === undefined || userId === ''
    ? 'anon' : `u${String(userId)}`);

/** Everything one user's keys start with. */
export const userKeyPrefix = (userId) => `${FROGGER_KEY_PREFIX}${userPart(userId)}.`;

/** The signed-in user's id off the auth context's `user`, or null. */
export const userIdOf = (user) => (
  user && user.id !== undefined && user.id !== null && user.id !== ''
    ? user.id : null);

/** THE key builders. `userId` is always first. */
export const froggerKeys = {
  /** One conversation's transcript (FroggerAIChatPanel). */
  log: (userId, facilityId, conversationId) => (
    `${userKeyPrefix(userId)}log.${facilityId || 'default'}.${conversationId}`),
  /** The conversation id a facility's chat is on (FroggerAIChatPanel). */
  conversation: (userId, facilityId) => (
    `${userKeyPrefix(userId)}cid.${facilityId || 'default'}`),
  /** The dock's sticky place (PlaceContext). */
  stickyPlace: (userId) => `${userKeyPrefix(userId)}${STICKY_PLACE_SUFFIX}`,
  /** The dock's open state (FroggerDock). */
  dockOpen: (userId) => `${userKeyPrefix(userId)}dock.open`,
  /** "This session already landed this junior on Froggy U" (juniorLanding). */
  juniorLanded: (userId) => `${userKeyPrefix(userId)}junior.landed`,
};

const storages = () => {
  const out = [];
  try { if (typeof window !== 'undefined' && window.sessionStorage) out.push(window.sessionStorage); } catch (e) { /* blocked */ }
  try { if (typeof window !== 'undefined' && window.localStorage) out.push(window.localStorage); } catch (e) { /* blocked */ }
  return out;
};

/** Is `key` a FroggerAI key that does not belong to `userId`? */
export const isForeignFroggerKey = (key, userId) => {
  const k = String(key || '');
  if (LEGACY_KEY_PREFIXES.some((p) => k.startsWith(p))) return true;
  if (!k.startsWith(FROGGER_KEY_PREFIX)) return false;
  if (userIdOf({ id: userId }) === null) return true;
  return !k.startsWith(userKeyPrefix(userId));
};

/**
 * Drop every FroggerAI key (session AND local storage) that is not
 * `userId`'s; with no user, drop them all. Never throws.
 */
export function clearForeignFroggerKeys(userId) {
  storages().forEach((store) => {
    try {
      const doomed = [];
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (isForeignFroggerKey(key, userId)) doomed.push(key);
      }
      doomed.forEach((key) => store.removeItem(key));
    } catch (e) { /* private mode */ }
  });
}
